import { Button, FormInput, Select } from '@getro/rombo';
import { roles } from 'helpers/roles';
import { ChevronRight, Plus, X } from 'lucide-react';
import React from 'react';
import { Box, Flex, Text } from 'rebass/styled-components';
import PropTypes from 'prop-types';
import { Form, Formik, Field } from 'formik';

export const Team = ({ users, setUsers, isLoadingAddAdmin, updateTeam }) => (
  <Flex
    sx={{
      mx: 'auto',
      mt: '100px',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '40px',
    }}
  >
    <Flex sx={{ flexDirection: 'column', gap: '16px', maxWidth: '400px' }}>
      <Text as="h1" fontSize="22px" fontWeight="600" lineHeight="1.2">
        Invite your team and unlock more intros
      </Text>
      <Text color="text.main" fontSize="16px">
        Invite your colleagues to expand your network and achieve more together.
      </Text>
    </Flex>

    <Flex sx={{ flexDirection: 'column', gap: '16px', maxWidth: '400px', justifyContent: 'flex-start' }}>
      <Formik
        validate={(values) => {
          const errors = {};
          Object.keys(values).forEach((key) => {
            if (values[key].email.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values[key].email)) {
              errors[key] = 'Email is required';
            }

            if (!values[key].role.value) {
              errors[key] = 'Role is required';
            }
          });

          return errors;
        }}
        validateOnBlur
        initialValues={users.reduce((acc, user) => {
          acc[user.id] = user;
          return acc;
        }, {})}
        onSubmit={(values) => {
          updateTeam({
            users,
            usersToAdd: Object.values(values)
              .map((user) => ({ email: user.email.trim(), role: user.role.value }))
              .filter((user) => user.email.length > 0),
          });
        }}
      >
        {({ handleSubmit, values, setFieldValue }) => (
          <Flex as={Form} sx={{ flexDirection: 'column', gap: '40px' }} onSubmit={handleSubmit}>
            <Flex sx={{ flexDirection: 'column', gap: '16px' }}>
              <Flex sx={{ gap: '8px', flexDirection: 'column' }}>
                {users?.map((user) => (
                  <Flex
                    key={user.id}
                    sx={{ flexDirection: 'row', gap: '8px', maxWidth: '400px', alignItems: 'baseline' }}
                  >
                    <Field
                      component={FormInput}
                      name={user.id}
                      placeholder="Enter email"
                      value={values?.[user.id]?.email ?? ''}
                      onChange={(e) => {
                        setFieldValue(user.id, {
                          ...user,
                          email: e.target.value,
                        });
                      }}
                    />
                    <Box sx={{ width: '300px' }}>
                      <Select
                        options={roles}
                        label="Select role"
                        dropdownWidth="300px"
                        value={user.role}
                        onChange={(e) => {
                          setUsers(users.map((u) => (u.id === user.id ? { ...u, role: e } : u)));
                          setFieldValue(user.id, {
                            ...values[user.id],
                            role: e,
                          });
                        }}
                      />
                    </Box>
                    {user.id > 1 ? (
                      <Box sx={{ cursor: 'pointer' }} onClick={() => setUsers(users.filter((u) => u.id !== user.id))}>
                        <Box as={X} size="16px" sx={{ color: 'text.main' }} />
                      </Box>
                    ) : (
                      <Box sx={{ width: '16px', height: '16px' }} />
                    )}
                  </Flex>
                ))}
              </Flex>
              <Flex
                sx={{
                  width: '100%',
                  justifyContent: 'flex-start',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  alignSelf: 'flex-start',
                  cursor: 'pointer',
                }}
                icon={Plus}
                onClick={() => setUsers([...users, { id: users.length + 1, name: '', email: '', role: roles[1] }])}
              >
                <Box as={Plus} size="16px" sx={{ color: 'text.main' }} />
                <Text fontSize="14px" fontWeight="400" lineHeight="1.4">
                  Add more
                </Text>
              </Flex>
            </Flex>

            <Button
              type="submit"
              variant="primary"
              sx={{ width: '100%', justifyContent: 'center' }}
              icon={ChevronRight}
              iconPosition="right"
              loading={isLoadingAddAdmin}
            >
              Continue
            </Button>

            <Button
              size="small"
              variant="secondary"
              sx={{ width: '100%', justifyContent: 'center', border: 'none', p: 0, fontSize: '14px' }}
              onClick={() =>
                updateTeam({
                  users,
                  usersToAdd: [],
                  skip: true,
                })
              }
            >
              Skip
            </Button>
          </Flex>
        )}
      </Formik>
    </Flex>
  </Flex>
);

Team.propTypes = {
  users: PropTypes.array.isRequired,
  setUsers: PropTypes.func.isRequired,
  isLoadingAddAdmin: PropTypes.bool.isRequired,
  updateTeam: PropTypes.func.isRequired,
};
